import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import PageLoadingSpinner from '../../components/PageLoadingSpinner';
import LinkIcon from '@mui/icons-material/Link';
import { 
  Button, 
  Typography, 
  CircularProgress, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  TablePagination,
  InputAdornment,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import getUser from "../../getUser";
import { useTranslation } from 'react-i18next';
import { FileUploadResultsDialog } from './FileUploadResultsDialog';
import { CustomRelationship, CustomRelationshipUpload, UploadResponse, UploadResponseFeedback, FileData, MappingConfig } from './types';
import { ErrorDialog } from './ErrorDialog';
import { useToast } from '../../components/Toast';
import { useDocumentTitle } from '../../hooks';
import { getCustomRelationshipSourceType, getCustomRelationshipType } from '../../i18n/backendTranslations';
import { formatDateTime } from '../../i18n/formatters';

const CustomLinking: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  useDocumentTitle(t('customLinking.documentTitle'));
  const [bulkInsertFile, setBulkInsertFile] = useState<File | null>(null);
  const [fileData, setFileData] = useState<FileData | null>(null);
  const [relationshipTypes, setRelationshipTypes] = useState<string[]>([]);
  const [selectedRelationshipType, setSelectedRelationshipType] = useState<string>('');
  
  // New states for column mapping
  const [showColumnMapping, setShowColumnMapping] = useState(false);
  const [mappingConfig, setMappingConfig] = useState<MappingConfig>({
    sourceTypeColumn: null,
    sourceValueColumn: null,
    startDateColumn: null,
    endDateColumn: null,
    defaultStartDate: '',
    defaultEndDate: '',
    relationshipMappings: {}
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedRelationship, setSelectedRelationship] = useState<CustomRelationship | null>(null);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
  const [allRelationships, setAllRelationships] = useState<CustomRelationship[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnFilters, setColumnFilters] = useState<{
    sourceType: string[];
    sourceValue: string[];
    relationshipType: string[];
    relationshipValue: string[];
    startDate: { from: string; to: string };
    endDate: { from: string; to: string };
  }>({
    sourceType: [],
    sourceValue: [],
    relationshipType: [],
    relationshipValue: [],
    startDate: { from: '', to: '' },
    endDate: { from: '', to: '' }
  });
  const [filterAnchorEls, setFilterAnchorEls] = useState<Record<string, HTMLElement | null>>({});
  const [uploadResults, setUploadResults] = useState<UploadResponseFeedback | null>(null);

  // Memoize the default filter state for reuse
  const defaultFilters = useMemo(() => ({
    sourceType: [] as string[],
    sourceValue: [] as string[],
    relationshipType: [] as string[],
    relationshipValue: [] as string[],
    startDate: { from: '', to: '' },
    endDate: { from: '', to: '' }
  }), []);

  // Memoize auth token to avoid repeated calls
  const authToken = useMemo(() => getUser()?.access_token || null, []);

  // Memoize lowercase search term to avoid repeated calculations
  const lowerSearchTerm = useMemo(() => searchTerm.toLowerCase(), [searchTerm]);

  // Function to convert Excel date serial number to date string
  const convertExcelDate = (value: any): string => {
    if (!value) return '';
    
    const stringValue = value.toString().trim();
    
    // If it's already a date string (YYYY-MM-DD format), return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
      return stringValue;
    }
    
    // Check if it's an Excel serial number (numeric and reasonable range)
    const numericValue = parseFloat(stringValue);
    if (!isNaN(numericValue) && numericValue > 1 && numericValue < 100000) {
      try {
        // Excel date serial number conversion
        // Excel epoch is January 1, 1900, but Excel incorrectly treats 1900 as a leap year
        const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
        const dateFromSerial = new Date(excelEpoch.getTime() + (numericValue * 24 * 60 * 60 * 1000));
        
        // Format as YYYY-MM-DD
        const year = dateFromSerial.getFullYear();
        const month = String(dateFromSerial.getMonth() + 1).padStart(2, '0');
        const day = String(dateFromSerial.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
      } catch (error) {
        return stringValue;
      }
    }
    
    return stringValue;
  };

  // Function to determine if an organization/person number represents a company or person
  // Based on DBT logic: returns true for company, false for person
  const isCompanyNumber = (organizationNumber: string): boolean => {
    if (!organizationNumber || typeof organizationNumber !== 'string') {
      return false;
    }

    const cleanNumber = organizationNumber.toString().trim().replace(/\D/g, ''); // Remove non-digits
    const length = cleanNumber.length;

    if (length === 10) {
      // For 10-digit numbers: check if first 2 digits are '55' AND digits 3-4 >= 20
      const firstTwo = cleanNumber.substring(0, 2);
      const thirdFourth = cleanNumber.substring(2, 4);
      const thirdFourthInt = parseInt(thirdFourth, 10);
      
      return firstTwo === '55' && thirdFourthInt >= 20;
    } 
    else if (length === 12) {
      // For 12-digit numbers: check if digits 3-4 are '55' AND digits 5-6 >= 20
      const thirdFourth = cleanNumber.substring(2, 4);
      const fifthSixth = cleanNumber.substring(4, 6);
      const fifthSixthInt = parseInt(fifthSixth, 10);
      
      return thirdFourth === '55' && fifthSixthInt >= 20;
    }
    else {
      return false;
    }
  };

  // Function to transform mapped data to relationships for upload
  const transformDataToRelationships = (): CustomRelationshipUpload[] => {
    if (!fileData) {
      return [];
    }

    const relationships: CustomRelationshipUpload[] = [];
    
    // Get column mappings
    const { sourceValueColumn, startDateColumn, endDateColumn, defaultStartDate, defaultEndDate, relationshipMappings } = mappingConfig;
    
    // Process each row
    fileData.rows.forEach((row, rowIndex) => {
      // Skip empty rows
      if (row.every(cell => !cell || cell.toString().trim() === '')) {
        return;
      }
      
      const sourceValue = sourceValueColumn !== null ? (row[sourceValueColumn] || '').toString().trim() : '';
      const startDate = startDateColumn !== null ? convertExcelDate(row[startDateColumn]) : defaultStartDate;
      const endDate = endDateColumn !== null && row[endDateColumn] ? convertExcelDate(row[endDateColumn]) : defaultEndDate;
      
      // Automatically determine source type based on organization/person number logic
      const sourceType = sourceValue ? (isCompanyNumber(sourceValue) ? 'company' : 'person') : '';
      
      // Skip rows missing required fields
      if (!sourceValue || !startDate) {
        return;
      }
      
      // Create relationships for each mapped relationship type
      Object.entries(relationshipMappings).forEach(([relationshipType, columnIndex]) => {
        if (columnIndex !== null) {
          const relationshipValue = (row[columnIndex] || '').toString().trim();
          if (relationshipValue) {
            const newRelationship = {
              sourceType,
              sourceValue,
              relationshipType,
              relationshipValue,
              startDate,
              ...(endDate ? { endDate } : {})
            };
            
            relationships.push(newRelationship);
          }
        }
      });
    });
    
    return relationships;
  };

  // Function to chunk array into smaller arrays
  const chunkArray = <T,>(array: T[], chunkSize: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const fetchRelationshipTypes = useCallback(async () => {
    setLoading(true);
    try {
      if (!authToken) {
        setError(t('customLinking.errors.unauthenticated'));
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/custom-relationships/types`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRelationshipTypes(data);
        if (data.length > 0) {
          setSelectedRelationshipType(data[0]);
        }
      } else {
        setError(t('customLinking.errors.fetchTypes'));
      }
    } catch (err) {
      setError(t('customLinking.errors.fetchTypes'));
    } finally {
      setLoading(false);
    }
  }, [authToken, t]);

  const fetchAllRelationships = useCallback(async (relationshipType: string): Promise<CustomRelationship[]> => {
    if (!authToken) {
      throw new Error(t('customLinking.errors.unauthenticated'));
    }

    let allRelationships: CustomRelationship[] = [];
    let cursor = 0;
    const limit = 100;
    let hasMore = true;

    while (hasMore) {
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/custom-relationships/?relationshipType=${relationshipType}&cursor=${cursor}&limit=${limit}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(t('customLinking.errors.fetchWithDetails', { details: await response.text() }));
      }

      const data = await response.json();
      allRelationships = [...allRelationships, ...data.relationships];
      
      // Check if there are more relationships to fetch
      hasMore = data.hasRemaining;
      cursor = data.cursor;
    }

    return allRelationships;
  }, [authToken, t]);

  const fetchAllRelationshipsAllTypes = useCallback(async () => {
    setLoading(true);
    try {
      if (!authToken) {
        setError(t('customLinking.errors.unauthenticated'));
        setLoading(false);
        return;
      }

      // Fetch ALL relationships for ALL types
      let allCombinedRelationships: CustomRelationship[] = [];
      
      for (const relationshipType of relationshipTypes) {
        try {
          const relationshipsForType = await fetchAllRelationships(relationshipType);
          allCombinedRelationships = [...allCombinedRelationships, ...relationshipsForType];
        } catch (err) {
          // Continue with other types even if one fails
        }
      }
      
      const formattedData = allCombinedRelationships.map((rel: CustomRelationship) => ({
        ...rel,
        createdAt: formatDateTime(rel.createdAt, i18n.language) ?? '',
      }));
      
      setAllRelationships(formattedData);
    } catch (err) {
      setError(t('customLinking.errors.fetch'));
    } finally {
      setLoading(false);
    }
  }, [authToken, fetchAllRelationships, i18n.language, relationshipTypes, t]);

  useEffect(() => {
    fetchRelationshipTypes();
  }, [fetchRelationshipTypes]);

  useEffect(() => {
    if (relationshipTypes.length > 0) {
      fetchAllRelationshipsAllTypes();
      setSelectedItems(new Set());
      setBulkDeleteMode(false);
      setCurrentPage(0);
      setSearchTerm('');
      setColumnFilters(defaultFilters);
    }
  }, [relationshipTypes, defaultFilters, fetchAllRelationshipsAllTypes]);



  const handleBulkInsertFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const fileName = file.name.toLowerCase();
      
      if (!fileName.endsWith('.csv') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        setError(t('customLinking.invalidFile'));
        return;
      }

      setBulkInsertFile(file);

      if (fileName.endsWith('.csv')) {
        // Handle CSV file with Papa Parse
        Papa.parse(file, {
          header: false, // Get arrays instead of objects
          skipEmptyLines: true,
          complete: (results: { data: string[][] }) => {
            processFileData(results.data);
          },
          error: () => {
            setError(t('customLinking.errors.parseCsv'));
          },
        });
      } else {
        // Handle Excel file with XLSX
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as string[][];
            processFileData(jsonData);
          } catch (error) {
            setError(t('customLinking.errors.parseExcel'));
          }
        };
        reader.readAsArrayBuffer(file);
      }
    } else {
      setBulkInsertFile(null);
      setFileData(null);
      setShowColumnMapping(false);
      setMappingConfig({
        sourceTypeColumn: null, // Keep for backward compatibility but not used
        sourceValueColumn: null,
        startDateColumn: null,
        endDateColumn: null,
        defaultStartDate: '',
        defaultEndDate: '',
        relationshipMappings: {}
      });
    }
  };

  const processFileData = (data: string[][]) => {
    // Make sure we have data 
    if (data.length < 1) {
      setError(t('customLinking.errors.headerRequired'));
      return;
    }
    
    // Extract headers and data rows
    const headers = data[0].map(header => (header || '').toString().trim());
    const rows = data.slice(1).filter(row => row.some(cell => cell && cell.toString().trim() !== ''));
    
    if (rows.length === 0) {
      setError(t('customLinking.errors.dataRequired'));
      return;
    }
    
    // Store file data and show column mapping interface
    setFileData({ headers, rows });
    setShowColumnMapping(true);
    
    // Initialize relationship mappings for available relationship types
    const initialRelationshipMappings: { [key: string]: number | null } = {};
    relationshipTypes.forEach(type => {
      initialRelationshipMappings[type] = null;
    });
    
    setMappingConfig(prev => ({
      ...prev,
      relationshipMappings: initialRelationshipMappings
    }));
  };

  const handleSubmitInsert = async () => {
    if (!bulkInsertFile) {
      setError(t('customLinking.errors.selectFile'));
      return;
    }

    if (!fileData) {
      setError(t('customLinking.errors.noFileData'));
      return;
    }

    // Validate mapping configuration
    const { sourceValueColumn, relationshipMappings } = mappingConfig;
    if (sourceValueColumn === null) {
      setError(t('customLinking.errors.mapSource'));
      return;
    }

    const mappedRelationshipTypes = Object.entries(relationshipMappings).filter(([_, columnIndex]) => columnIndex !== null);
    if (mappedRelationshipTypes.length === 0) {
      setError(t('customLinking.mapOne'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      if (!authToken) {
        setError(t('customLinking.errors.unauthenticated'));
        setLoading(false);
        return;
      }

      // Transform data based on mapping configuration
      const allRelationships = transformDataToRelationships();
      
      if (allRelationships.length === 0) {
        setError(t('customLinking.errors.noValidRows'));
        setLoading(false);
        return;
      }
      
      // Group relationships by type
      const relationshipsByType: { [key: string]: CustomRelationshipUpload[] } = {};
      allRelationships.forEach(rel => {
        if (!relationshipsByType[rel.relationshipType]) {
          relationshipsByType[rel.relationshipType] = [];
        }
        relationshipsByType[rel.relationshipType].push(rel);
      });

      // Deduplicate relationships within each type
      const deduplicatedRelationshipsByType: { [key: string]: CustomRelationshipUpload[] } = {};

      Object.entries(relationshipsByType).forEach(([relationshipType, relationships]) => {
        const uniqueRelationships = new Map<string, CustomRelationshipUpload>();

        relationships.forEach(rel => {
          // Create a unique key based on all relationship fields
          const uniqueKey = `${rel.sourceType}|${rel.sourceValue}|${rel.relationshipType}|${rel.relationshipValue}|${rel.startDate}|${rel.endDate || 'NO_END_DATE'}`;
          
          if (!uniqueRelationships.has(uniqueKey)) {
            uniqueRelationships.set(uniqueKey, rel);
          }
        });

        deduplicatedRelationshipsByType[relationshipType] = Array.from(uniqueRelationships.values());
      });

      const totalRelationshipTypes = Object.keys(deduplicatedRelationshipsByType).length;
      let processedTypes = 0;

      // Initialize progress tracking
      setUploadProgress({ current: 0, total: totalRelationshipTypes });

      // Process each relationship type sequentially
      const uploadResponseFeedback: UploadResponseFeedback = {
        successCount: 0,
        alreadyExistsCount: 0,
        failedRelationships: []
      };

      var errorMessage = null;

      for (const [relationshipType, relationships] of Object.entries(deduplicatedRelationshipsByType)) {
        // Split relationships into chunks of 100
        const chunks = chunkArray(relationships, 100);
        
        for (let i = 0; i < chunks.length; i++) {
          const chunk = chunks[i];
        
          const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/custom-relationships`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(chunk),
          });

          if (response.ok) {
            const uploadResponse: UploadResponse = await response.json();
            
            // Check for "already exists" errors specifically
            const alreadyExistsErrors = uploadResponse.errors.filter(error => error.errorCode?.toLowerCase() === 'alreadyexists');
            
            uploadResponseFeedback.successCount += uploadResponse.successes.length;
            uploadResponseFeedback.alreadyExistsCount += alreadyExistsErrors.length;
            // Note that we only show the first 20 errors in the feedback to the user,
            // since there could be thousands.
            const errorsToFeedback = uploadResponse.errors.filter(error => error.errorCode?.toLowerCase() !== 'alreadyexists');
            for (let j = 0; j < errorsToFeedback.length && uploadResponseFeedback.failedRelationships.length < 20; j++) {
              uploadResponseFeedback.failedRelationships.push(errorsToFeedback[j]);
            }
          } else if (response.status === 400) {
            const errorDetails = await response.text();
            errorMessage = t('customLinking.errors.uploadSchema', { type: getCustomRelationshipType(relationshipType), details: errorDetails });
            break;
          } else {
            //const errorDetails = await response.text();
            errorMessage = t('customLinking.errors.uploadType', { type: getCustomRelationshipType(relationshipType) });
            break;
          }
        }
        
        if (errorMessage) break;
        
        processedTypes++;
        setUploadProgress({ current: processedTypes, total: totalRelationshipTypes });
      }

      // If we don't already have an error, then we want to show the upload results.
      if (!errorMessage) {
        setUploadResults(uploadResponseFeedback);
      } else {
        setError(errorMessage);
      }
      setUploadProgress(null);
      fetchAllRelationshipsAllTypes();
      setBulkInsertFile(null);
      setFileData(null);
      setShowColumnMapping(false);
      setMappingConfig({
        sourceTypeColumn: null, // Keep for backward compatibility but not used
        sourceValueColumn: null,
        startDateColumn: null,
        endDateColumn: null,
        defaultStartDate: '',
        defaultEndDate: '',
        relationshipMappings: {}
      });
    }
    catch (err) {
      if (err instanceof Error) {
        setError(t('customLinking.errors.insertWithDetails', { details: err.message }));
      } else {
        setError(t('customLinking.errors.insert'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (relationship: CustomRelationship) => {
    setError(null);
    setLoading(true);

    try {
      if (!authToken) {
        setError(t('customLinking.errors.unauthenticated'));
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/custom-relationships/${relationship.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        const errorMessage = await response.text();
        setError(t('customLinking.errors.removeWithDetails', { details: errorMessage }));
      } else {
        toast.success(t('customLinking.messages.removed'));
        fetchAllRelationshipsAllTypes();
      }
    } catch (err) {
      setError(t('customLinking.errors.remove'));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setError(null);
    setLoading(true);

    try {
      if (!authToken) {
        setError(t('customLinking.errors.unauthenticated'));
        setLoading(false);
        return;
      }

      const relationshipsToDelete = Array.from(selectedItems);

      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/custom-relationships/delete-many`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(relationshipsToDelete),
      });

      if (!response.ok) {
        const errorMessage = await response.text();
        setError(t('customLinking.errors.deleteWithDetails', { details: errorMessage }));
      } else {
        toast.success(t('customLinking.messages.selectedDeleted'));
        fetchAllRelationshipsAllTypes();
        setBulkDeleteMode(false);
        setSelectedItems(new Set());
      }
    } catch (err) {
      setError(t('customLinking.errors.bulkDelete'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    setError(null);
    setLoading(true);

    try {
      if (!authToken) {
        setError(t('customLinking.errors.unauthenticated'));
        setLoading(false);
        return;
      }

      // Use current relationships data (all types)
      const allRelationshipsData = allRelationships;
      
      if (allRelationshipsData.length === 0) {
        toast.warning(t('customLinking.messages.nothingToDelete'));
        setLoading(false);
        setDeleteAllDialogOpen(false);
        setDeleteAllConfirmText('');
        return;
      }

      const allRelationshipIds = allRelationshipsData.map(rel => rel.id);
      
      // Split the IDs into chunks of 100 for deletion
      const chunks = chunkArray(allRelationshipIds, 100);
      
      let successfulBatches = 0;
      let failedBatches = 0;
      const errors: string[] = [];

      // Initialize progress tracking
      setUploadProgress({ current: 0, total: chunks.length });

      // Process each chunk sequentially
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        
        // Update progress
        setUploadProgress({ current: i + 1, total: chunks.length });
        
        try {
          const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/custom-relationships/delete-many`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(chunk),
          });

          if (!response.ok) {
            const errorMessage = await response.text();
            errors.push(t('customLinking.errors.batchWithDetails', { current: i + 1, total: chunks.length, details: errorMessage }));
            failedBatches++;
          } else {
            successfulBatches++;
          }
        } catch (batchError) {
          errors.push(t('customLinking.errors.batchNetwork', { current: i + 1, total: chunks.length }));
          failedBatches++;
        }
      }

      // Clear progress
      setUploadProgress(null);

      // Show results
      if (failedBatches === 0) {
        toast.success(t('customLinking.messages.allDeleted', { count: allRelationshipsData.length }));
        fetchAllRelationshipsAllTypes();
        setBulkDeleteMode(false);
        setSelectedItems(new Set());
        setDeleteAllDialogOpen(false);
        setDeleteAllConfirmText('');
      } else if (successfulBatches > 0) {
        const errorDetails = errors.join('\n');
        setError(t('customLinking.errors.partialSuccess', { successful: successfulBatches, failed: failedBatches, details: errorDetails }));
        fetchAllRelationshipsAllTypes();
        setDeleteAllDialogOpen(false);
        setDeleteAllConfirmText('');
      } else {
        const errorDetails = errors.join('\n');
        setError(t('customLinking.errors.allBatchesFailed', { details: errorDetails }));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('customLinking.errors.deleteAll');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openConfirmDialog = (relationship: CustomRelationship) => {
    setSelectedRelationship(relationship);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setSelectedRelationship(null);
    setConfirmDialogOpen(false);
  };

  const openDeleteAllDialog = () => {
    setDeleteAllDialogOpen(true);
    setDeleteAllConfirmText('');
  };

  const closeDeleteAllDialog = () => {
    setDeleteAllDialogOpen(false);
    setDeleteAllConfirmText('');
  };

  const toggleBulkDeleteMode = () => {
    setBulkDeleteMode(!bulkDeleteMode);
    setSelectedItems(new Set());
  };

  const handleItemSelect = (relationship: CustomRelationship) => {
    const key = relationship.id;
    setSelectedItems(prev => {
      const newSelectedItems = new Set(prev);
      if (newSelectedItems.has(key)) {
        newSelectedItems.delete(key);
      } else {
        newSelectedItems.add(key);
      }
      return newSelectedItems;
    });
  };

  // Filter and paginate relationships
  const filteredRelationships = useMemo(() => {
    return allRelationships.filter(rel => {
      // Global search filter
      const matchesGlobalSearch = !lowerSearchTerm || (
        rel.sourceType.toLowerCase().includes(lowerSearchTerm) ||
        rel.sourceValue.toLowerCase().includes(lowerSearchTerm) ||
        rel.relationshipValue.toLowerCase().includes(lowerSearchTerm) ||
        rel.startDate.toLowerCase().includes(lowerSearchTerm) ||
        (rel.endDate && rel.endDate.toLowerCase().includes(lowerSearchTerm)) ||
        rel.createdAt.toLowerCase().includes(lowerSearchTerm)
      );

      // Column-specific filters
      const matchesNodeType = columnFilters.sourceType.length === 0 || columnFilters.sourceType.includes(rel.sourceType);
      const matchesNodeValue = columnFilters.sourceValue.length === 0 || columnFilters.sourceValue.includes(rel.sourceValue);
      const matchesRelationshipType = columnFilters.relationshipType.length === 0 || columnFilters.relationshipType.includes(rel.relationshipType);
      const matchesRelationshipValue = columnFilters.relationshipValue.length === 0 || columnFilters.relationshipValue.includes(rel.relationshipValue);
      
      // Date filters
      const matchesStartDate = (!columnFilters.startDate.from || rel.startDate >= columnFilters.startDate.from) &&
                              (!columnFilters.startDate.to || rel.startDate <= columnFilters.startDate.to);
      const matchesEndDate = (!columnFilters.endDate.from || (rel.endDate && rel.endDate >= columnFilters.endDate.from)) &&
                            (!columnFilters.endDate.to || (rel.endDate && rel.endDate <= columnFilters.endDate.to));

      return matchesGlobalSearch && matchesNodeType && matchesNodeValue && matchesRelationshipType && matchesRelationshipValue && matchesStartDate && matchesEndDate;
    });
  }, [allRelationships, lowerSearchTerm, columnFilters]);

  const paginatedRelationships = useMemo(() => {
    return filteredRelationships.slice(
      currentPage * rowsPerPage,
      currentPage * rowsPerPage + rowsPerPage
    );
  }, [filteredRelationships, currentPage, rowsPerPage]);

  // Get unique values for dropdown filters
  const getUniqueValues = useCallback((field: 'sourceType' | 'sourceValue' | 'relationshipType' | 'relationshipValue') => {
    const uniqueSet = new Set(allRelationships.map(rel => rel[field]).filter(value => value && value.trim() !== ''));
    return Array.from(uniqueSet).sort();
  }, [allRelationships]);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>, column: string) => {
    setFilterAnchorEls(prev => ({ ...prev, [column]: event.currentTarget }));
  };

  const handleFilterClose = (column: string) => {
    setFilterAnchorEls(prev => ({ ...prev, [column]: null }));
  };

  const handleColumnFilterChange = (column: 'sourceType' | 'sourceValue' | 'relationshipType' | 'relationshipValue', value: string) => {
    setColumnFilters(prev => {
      const currentValues = prev[column];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      
      return { ...prev, [column]: newValues };
    });
    setCurrentPage(0);
  };

  const handleDateFilterChange = (column: 'startDate' | 'endDate', type: 'from' | 'to', value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: { ...prev[column], [type]: value }
    }));
    setCurrentPage(0);
  };

  const clearColumnFilter = useCallback((column: string) => {
    setColumnFilters(prev => {
      if (column === 'startDate' || column === 'endDate') {
        return { ...prev, [column]: { from: '', to: '' } };
      } else {
        return { ...prev, [column]: [] };
      }
    });
    setCurrentPage(0);
  }, []);

  const hasActiveFilters = useCallback(() => {
    return columnFilters.sourceType.length > 0 ||
           columnFilters.sourceValue.length > 0 ||
           columnFilters.relationshipType.length > 0 ||
           columnFilters.relationshipValue.length > 0 ||
           columnFilters.startDate.from !== '' ||
           columnFilters.startDate.to !== '' ||
           columnFilters.endDate.from !== '' ||
           columnFilters.endDate.to !== '';
  }, [columnFilters]);

  const clearAllFilters = useCallback(() => {
    setColumnFilters(defaultFilters);
    setSearchTerm('');
    setCurrentPage(0);
  }, [defaultFilters]);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(0); // Reset to first page when searching
  }, []);

  return (
    <Box sx={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', '& > div': { maxWidth: '100%' }, '& div:has(> input[type="file"])': { flexWrap: 'wrap' } }}>
      <ErrorDialog message={error} onClose={() => setError(null)} />
      <FileUploadResultsDialog uploadResponseFeedback={uploadResults} onClose={() => setUploadResults(null)} />
      
      <PageHeader title={t('customLinking.title')} icon={<LinkIcon />} />
      <Paper component="details" variant="outlined" sx={{ width: '100%', p: 2, mb: 3 }}>
        <Typography component="summary" variant="subtitle2" sx={{ cursor: 'pointer', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 4 } }}>{t('customLinking.instructions.title')}</Typography>
        <Typography variant="body2" sx={{ mt: 2, mb: 2 }}>
          {t('customLinking.instructions.intro')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('customLinking.instructions.required')}
        </Typography>        
        <Box sx={{ ml: 2, mb: 2 }}>
          <Typography variant="body2" component="li">
            <strong>{t('customLinking.sourceValue')}:</strong> {t('customLinking.instructions.source')}
          </Typography>
          <Typography variant="body2" component="li">
            <strong>{t('common.fields.startDate')}:</strong> {t('customLinking.instructions.startDate')}
          </Typography>
          <Typography variant="body2" component="li">
            <strong>{t('customLinking.relationshipTypes')}:</strong> {t('customLinking.instructions.relationshipTypes')}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {t('customLinking.instructions.endDate')}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom>
        {t('customLinking.upload')}
      </Typography>
      <Box sx={{ width: '100%', maxWidth: '1400px', mb: 3 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', marginBottom: 'var(--spacing-md)' }}>
          <input
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            id="bulk-insert-file"
            type="file"
            onChange={handleBulkInsertFileChange}
          />
            <Button variant="outlined" disabled={loading} onClick={() => document.getElementById('bulk-insert-file')?.click()}>
              {t('customLinking.chooseFile')}
            </Button>
          {bulkInsertFile && <Typography variant="body1">{t('customLinking.selectedFile', { name: bulkInsertFile.name })}</Typography>}
          {fileData && (
            <Typography variant="body2" color="textSecondary">
              {t('customLinking.fileSize', { rows: fileData.rows.length, columns: fileData.headers.length })}
            </Typography>
          )}
          {uploadProgress && (
            <Typography variant="body2" color="primary">
              {t('customLinking.processing', uploadProgress)}
            </Typography>
          )}
        </div>

        {/* Column Mapping Interface */}
        {showColumnMapping && fileData && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('customLinking.mapping.title')}
            </Typography>
            
            {/* Required Field Mappings */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('customLinking.mapping.requiredFields')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>{t('customLinking.mapping.sourceColumn')}</InputLabel>
                  <Select
                    value={mappingConfig.sourceValueColumn ?? ''}
                    onChange={(e) => setMappingConfig(prev => ({ ...prev, sourceValueColumn: e.target.value === '' ? null : Number(e.target.value) }))}
                    label={t('customLinking.mapping.sourceColumn')}
                  >
                    <MenuItem value="">
                      <em>{t('common.states.notAvailable')}</em>
                    </MenuItem>
                    {fileData.headers.map((header, index) => (
                      <MenuItem key={index} value={index}>
                        {header || t('customLinking.mapping.column', { number: index + 1 })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>{t('customLinking.mapping.startDateColumn')}</InputLabel>
                  <Select
                    value={mappingConfig.startDateColumn ?? ''}
                    onChange={(e) => setMappingConfig(prev => ({ ...prev, startDateColumn: e.target.value === '' ? null : Number(e.target.value) }))}
                    label={t('customLinking.mapping.startDateColumn')}
                  >
                    <MenuItem value="">
                      <em>{t('common.states.notAvailable')}</em>
                    </MenuItem>
                    {fileData.headers.map((header, index) => (
                      <MenuItem key={index} value={index}>
                        {header || t('customLinking.mapping.column', { number: index + 1 })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            
            {/* Optional Field Mappings */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('customLinking.mapping.optionalFields')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>{t('customLinking.mapping.endDateColumn')}</InputLabel>
                  <Select
                    value={mappingConfig.endDateColumn ?? ''}
                    onChange={(e) => setMappingConfig(prev => ({ ...prev, endDateColumn: e.target.value === '' ? null : Number(e.target.value) }))}
                    label={t('customLinking.mapping.endDateColumn')}
                  >
                    <MenuItem value="">
                      <em>{t('common.states.notAvailable')}</em>
                    </MenuItem>
                    {fileData.headers.map((header, index) => (
                      <MenuItem key={index} value={index}>
                        {header || t('customLinking.mapping.column', { number: index + 1 })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  label={t('customLinking.mapping.defaultStartDate')}
                  type="date"
                  value={mappingConfig.defaultStartDate}
                  onChange={(e) => setMappingConfig(prev => ({ ...prev, defaultStartDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                />
                
                <TextField
                  label={t('customLinking.mapping.defaultEndDate')}
                  type="date"
                  value={mappingConfig.defaultEndDate}
                  onChange={(e) => setMappingConfig(prev => ({ ...prev, defaultEndDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                />
              </Box>
            </Box>
            
            {/* Relationship Type Mappings */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('customLinking.mapping.relationshipMappings')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {relationshipTypes.map(relationshipType => (
                  <FormControl key={relationshipType} sx={{ minWidth: 200 }}>
                    <InputLabel>{t('customLinking.mapping.typeColumn', { type: getCustomRelationshipType(relationshipType) })}</InputLabel>
                    <Select
                      value={mappingConfig.relationshipMappings[relationshipType] ?? ''}
                      onChange={(e) => {
                        const newValue = e.target.value === '' ? null : Number(e.target.value);
                        setMappingConfig(prev => ({ 
                          ...prev, 
                          relationshipMappings: {
                            ...prev.relationshipMappings,
                            [relationshipType]: newValue
                          }
                        }));
                      }}
                      label={t('customLinking.mapping.typeColumn', { type: getCustomRelationshipType(relationshipType) })}
                    >
                      <MenuItem value="">
                        <em>{t('common.states.notAvailable')}</em>
                      </MenuItem>
                      {fileData.headers.map((header, index) => (
                        <MenuItem key={index} value={index}>
                          {header || t('customLinking.mapping.column', { number: index + 1 })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Box>
            </Box>
            
            {/* Preview Table */}
            {fileData.rows.length > 0 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  {t('customLinking.mapping.preview')}
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 400, overflowX: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {fileData.headers.map((header, index) => (
                          <TableCell key={index} sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>
                            {header || t('customLinking.mapping.column', { number: index + 1 })}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fileData.rows.slice(0, 5).map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {row.map((cell, cellIndex) => (
                            <TableCell key={cellIndex} sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>
                              {cell || ''}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitInsert}
              disabled={loading || mappingConfig.sourceValueColumn === null || Object.values(mappingConfig.relationshipMappings).every(v => v === null)}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : t('customLinking.upload')}
            </Button>
          </Paper>
        )}
      </Box>

      <Typography variant="h6" gutterBottom style={{ marginTop: 'var(--spacing-md)' }}>
        {t('customLinking.existing', { count: allRelationships.length })}
      </Typography>
      
      <Box sx={{ width: '100%', maxWidth: '1400px', mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            variant="outlined"
            placeholder={t('customLinking.globalSearch')}
            size="small"
            inputProps={{ 'aria-label': t('customLinking.globalSearch') }}
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: '1 1 240px', minWidth: 0 }}
          />
          {hasActiveFilters() && (
            <Button
              variant="outlined"
              onClick={clearAllFilters}
              startIcon={<ClearIcon />}
              size="small"
            >
              {t('customLinking.clearAllFilters')}
            </Button>
          )}
        </Box>
        
        {/* Active filters display */}
        {hasActiveFilters() && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {columnFilters.sourceType.map(value => (
              <Chip
                key={`sourceType-${value}`}
                label={t('customLinking.filterValue', { field: t('customLinking.nodeType'), value: getCustomRelationshipSourceType(value) })}
                onDelete={() => handleColumnFilterChange('sourceType', value)}
                size="small"
                variant="filled"
                color="primary"
                sx={{ 
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiChip-deleteIcon': {
                    color: 'primary.contrastText'
                  }
                }}
              />
            ))}
            {columnFilters.sourceValue.map(value => (
              <Chip
                key={`sourceValue-${value}`}
                label={t('customLinking.filterValue', { field: t('customLinking.nodeValue'), value })}
                onDelete={() => handleColumnFilterChange('sourceValue', value)}
                size="small"
                variant="filled"
                color="primary"
                sx={{ 
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiChip-deleteIcon': {
                    color: 'primary.contrastText'
                  }
                }}
              />
            ))}
            {columnFilters.relationshipType.map(value => (
              <Chip
                key={`relationshipType-${value}`}
                label={t('customLinking.filterValue', { field: t('customLinking.relationshipType'), value: getCustomRelationshipType(value) })}
                onDelete={() => handleColumnFilterChange('relationshipType', value)}
                size="small"
                variant="filled"
                color="primary"
                sx={{ 
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiChip-deleteIcon': {
                    color: 'primary.contrastText'
                  }
                }}
              />
            ))}
            {columnFilters.relationshipValue.map(value => (
              <Chip
                key={`relationshipValue-${value}`}
                label={t('customLinking.filterValue', { field: t('customLinking.relationshipValue'), value })}
                onDelete={() => handleColumnFilterChange('relationshipValue', value)}
                size="small"
                variant="filled"
                color="primary"
                sx={{ 
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiChip-deleteIcon': {
                    color: 'primary.contrastText'
                  }
                }}
              />
            ))}
            {(columnFilters.startDate.from || columnFilters.startDate.to) && (
              <Chip
                label={t('customLinking.dateFilterValue', { field: t('common.fields.startDate'), from: columnFilters.startDate.from || '…', to: columnFilters.startDate.to || '…' })}
                onDelete={() => clearColumnFilter('startDate')}
                size="small"
                variant="filled"
                color="secondary"
                sx={{ 
                  bgcolor: 'secondary.main',
                  color: 'secondary.contrastText',
                  '& .MuiChip-deleteIcon': {
                    color: 'secondary.contrastText'
                  }
                }}
              />
            )}
            {(columnFilters.endDate.from || columnFilters.endDate.to) && (
              <Chip
                label={t('customLinking.dateFilterValue', { field: t('common.fields.endDate'), from: columnFilters.endDate.from || '…', to: columnFilters.endDate.to || '…' })}
                onDelete={() => clearColumnFilter('endDate')}
                size="small"
                variant="filled"
                color="secondary"
                sx={{ 
                  bgcolor: 'secondary.main',
                  color: 'secondary.contrastText',
                  '& .MuiChip-deleteIcon': {
                    color: 'secondary.contrastText'
                  }
                }}
              />
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ width: '100%', maxWidth: '1400px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={bulkDeleteMode} 
                onChange={toggleBulkDeleteMode}
                disabled={loading || allRelationships.length === 0}
              />
            }
            label={t('customLinking.selectMultiple')}
          />
          {allRelationships.length > 0 && (
            <Button 
              variant="outlined" 
              color="error" 
              onClick={openDeleteAllDialog}
              disabled={loading}
              size="small"
            >
              {t('customLinking.deleteAllWithCount', { count: allRelationships.length })}
            </Button>
          )}
        </Box>
        {bulkDeleteMode && selectedItems.size > 0 && (
          <Button 
            variant="contained" 
            color="secondary" 
            onClick={handleBulkDelete}
            disabled={loading}
          >
            {t('customLinking.deleteSelected', { count: selectedItems.size })}
          </Button>
        )}
      </Box>
      
      {loading ? (
        <PageLoadingSpinner variant="inline" label={t('common.states.loading')} />
      ) : (
        <TableContainer component={Paper} style={{ marginTop: 'var(--spacing-md)', width: '100%', maxWidth: '1400px', overflowX: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {bulkDeleteMode && <TableCell padding="checkbox"></TableCell>}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {t('customLinking.nodeType')}
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, 'sourceType')}
                      color={columnFilters.sourceType.length > 0 ? 'primary' : 'default'}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {t('customLinking.nodeValue')}
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, 'sourceValue')}
                      color={columnFilters.sourceValue.length > 0 ? 'primary' : 'default'}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {t('customLinking.relationshipType')}
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, 'relationshipType')}
                      color={columnFilters.relationshipType.length > 0 ? 'primary' : 'default'}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {t('customLinking.relationshipValue')}
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, 'relationshipValue')}
                      color={columnFilters.relationshipValue.length > 0 ? 'primary' : 'default'}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {t('common.fields.startDate')}
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, 'startDate')}
                      color={columnFilters.startDate.from || columnFilters.startDate.to ? 'primary' : 'default'}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {t('common.fields.endDate')}
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, 'endDate')}
                      color={columnFilters.endDate.from || columnFilters.endDate.to ? 'primary' : 'default'}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>{t('common.fields.created')}</TableCell>
                {!bulkDeleteMode && <TableCell>{t('common.fields.actions')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRelationships.length > 0 ? (
                paginatedRelationships.map((rel, index) => {
                  return (
                    <TableRow key={rel.id}>
                      {bulkDeleteMode && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedItems.has(rel.id)}
                            onChange={() => handleItemSelect(rel)}
                          />
                        </TableCell>
                      )}
                      <TableCell>{getCustomRelationshipSourceType(rel.sourceType)}</TableCell>
                      <TableCell>{rel.sourceValue}</TableCell>
                      <TableCell>{getCustomRelationshipType(rel.relationshipType)}</TableCell>
                      <TableCell>{rel.relationshipValue}</TableCell>
                      <TableCell>{rel.startDate}</TableCell>
                      <TableCell>{rel.endDate || '-'}</TableCell>
                      <TableCell>{rel.createdAt}</TableCell>
                      {!bulkDeleteMode && (
                        <TableCell>
                          <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() => openConfirmDialog(rel)}
                          >
                            {t('common.actions.delete')}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={bulkDeleteMode ? 9 : 8} align="center">
                    <EmptyState icon={<LinkIcon />} title={searchTerm ? t('customLinking.noSearchResults', { search: searchTerm }) : t('customLinking.noResults')} sx={{ py: 4 }} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {allRelationships.length > 0 && (
        <TablePagination
          component="div"
          count={filteredRelationships.length}
          page={currentPage}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ width: '100%', maxWidth: '1400px' }}
        />
      )}

      {/* Filter Popovers */}
      {/* Node Type Filter */}
      <Popover
        open={Boolean(filterAnchorEls.sourceType)}
        anchorEl={filterAnchorEls.sourceType}
        onClose={() => handleFilterClose('sourceType')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: { 
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider'
          }
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.primary' }}>
            {t('customLinking.filterTitle', { field: t('customLinking.nodeType') })}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List dense>
            {getUniqueValues('sourceType').map(value => (
              <ListItem key={value} disablePadding>
                <ListItemButton 
                  onClick={() => handleColumnFilterChange('sourceType', value)}
                  sx={{ 
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 1
                  }}
                >
                  <Checkbox
                    checked={columnFilters.sourceType.includes(value)}
                    size="small"
                    sx={{ color: 'primary.main' }}
                  />
                  <ListItemText 
                    primary={getCustomRelationshipSourceType(value)}
                    sx={{ '& .MuiListItemText-primary': { color: 'text.primary' } }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {columnFilters.sourceType.length > 0 && (
            <Button
              size="small"
              onClick={() => clearColumnFilter('sourceType')}
              sx={{ mt: 1 }}
              variant="outlined"
            >
              {t('common.actions.reset')}
            </Button>
          )}
        </Box>
      </Popover>

      {/* Node Value Filter */}
      <Popover
        open={Boolean(filterAnchorEls.sourceValue)}
        anchorEl={filterAnchorEls.sourceValue}
        onClose={() => handleFilterClose('sourceValue')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: { 
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider'
          }
        }}
      >
        <Box sx={{ p: 2, minWidth: 200, maxHeight: 400, overflowY: 'auto' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.primary' }}>
            {t('customLinking.filterTitle', { field: t('customLinking.nodeValue') })}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List dense>
            {getUniqueValues('sourceValue').map(value => (
              <ListItem key={value} disablePadding>
                <ListItemButton 
                  onClick={() => handleColumnFilterChange('sourceValue', value)}
                  sx={{ 
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 1
                  }}
                >
                  <Checkbox
                    checked={columnFilters.sourceValue.includes(value)}
                    size="small"
                    sx={{ color: 'primary.main' }}
                  />
                  <ListItemText 
                    primary={value} 
                    sx={{ '& .MuiListItemText-primary': { color: 'text.primary' } }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {columnFilters.sourceValue.length > 0 && (
            <Button
              size="small"
              onClick={() => clearColumnFilter('sourceValue')}
              sx={{ mt: 1 }}
              variant="outlined"
            >
              {t('common.actions.reset')}
            </Button>
          )}
        </Box>
      </Popover>

      {/* Relationship Type Filter */}
      <Popover
        open={Boolean(filterAnchorEls.relationshipType)}
        anchorEl={filterAnchorEls.relationshipType}
        onClose={() => handleFilterClose('relationshipType')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: { 
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider'
          }
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.primary' }}>
            {t('customLinking.filterTitle', { field: t('customLinking.relationshipType') })}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List dense>
            {getUniqueValues('relationshipType').map(value => (
              <ListItem key={value} disablePadding>
                <ListItemButton 
                  onClick={() => handleColumnFilterChange('relationshipType', value)}
                  sx={{ 
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 1
                  }}
                >
                  <Checkbox
                    checked={columnFilters.relationshipType.includes(value)}
                    size="small"
                    sx={{ color: 'primary.main' }}
                  />
                  <ListItemText 
                    primary={getCustomRelationshipType(value)}
                    sx={{ '& .MuiListItemText-primary': { color: 'text.primary' } }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {columnFilters.relationshipType.length > 0 && (
            <Button
              size="small"
              onClick={() => clearColumnFilter('relationshipType')}
              sx={{ mt: 1 }}
              variant="outlined"
            >
              {t('common.actions.reset')}
            </Button>
          )}
        </Box>
      </Popover>

      {/* Relationship Value Filter */}
      <Popover
        open={Boolean(filterAnchorEls.relationshipValue)}
        anchorEl={filterAnchorEls.relationshipValue}
        onClose={() => handleFilterClose('relationshipValue')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: { 
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider'
          }
        }}
      >
        <Box sx={{ p: 2, minWidth: 200, maxHeight: 400, overflowY: 'auto' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.primary' }}>
            {t('customLinking.filterTitle', { field: t('customLinking.relationshipValue') })}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List dense>
            {getUniqueValues('relationshipValue').map(value => (
              <ListItem key={value} disablePadding>
                <ListItemButton 
                  onClick={() => handleColumnFilterChange('relationshipValue', value)}
                  sx={{ 
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 1
                  }}
                >
                  <Checkbox
                    checked={columnFilters.relationshipValue.includes(value)}
                    size="small"
                    sx={{ color: 'primary.main' }}
                  />
                  <ListItemText 
                    primary={value} 
                    sx={{ '& .MuiListItemText-primary': { color: 'text.primary' } }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {columnFilters.relationshipValue.length > 0 && (
            <Button
              size="small"
              onClick={() => clearColumnFilter('relationshipValue')}
              sx={{ mt: 1 }}
              variant="outlined"
            >
              {t('common.actions.reset')}
            </Button>
          )}
        </Box>
      </Popover>

      {/* Start Date Filter */}
      <Popover
        open={Boolean(filterAnchorEls.startDate)}
        anchorEl={filterAnchorEls.startDate}
        onClose={() => handleFilterClose('startDate')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: { 
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider'
          }
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.primary' }}>
            {t('customLinking.filterTitle', { field: t('common.fields.startDate') })}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField
            label={t('customLinking.fromDate')}
            type="date"
            value={columnFilters.startDate.from}
            onChange={(e) => handleDateFilterChange('startDate', 'from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('customLinking.toDate')}
            type="date"
            value={columnFilters.startDate.to}
            onChange={(e) => handleDateFilterChange('startDate', 'to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mb: 2 }}
          />
          {(columnFilters.startDate.from || columnFilters.startDate.to) && (
            <Button
              size="small"
              onClick={() => clearColumnFilter('startDate')}
              variant="outlined"
            >
              {t('common.actions.reset')}
            </Button>
          )}
        </Box>
      </Popover>

      {/* End Date Filter */}
      <Popover
        open={Boolean(filterAnchorEls.endDate)}
        anchorEl={filterAnchorEls.endDate}
        onClose={() => handleFilterClose('endDate')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: { 
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider'
          }
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.primary' }}>
            {t('customLinking.filterTitle', { field: t('common.fields.endDate') })}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField
            label={t('customLinking.fromDate')}
            type="date"
            value={columnFilters.endDate.from}
            onChange={(e) => handleDateFilterChange('endDate', 'from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('customLinking.toDate')}
            type="date"
            value={columnFilters.endDate.to}
            onChange={(e) => handleDateFilterChange('endDate', 'to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mb: 2 }}
          />
          {(columnFilters.endDate.from || columnFilters.endDate.to) && (
            <Button
              size="small"
              onClick={() => clearColumnFilter('endDate')}
              variant="outlined"
            >
              {t('common.actions.reset')}
            </Button>
          )}
        </Box>
      </Popover>

      <Dialog
        open={confirmDialogOpen}
        onClose={closeConfirmDialog}
      >
        <DialogTitle>{t('customLinking.confirmDelete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('customLinking.confirmDelete.body')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} color="primary">
            {t('common.actions.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (selectedRelationship) handleRemove(selectedRelationship);
              closeConfirmDialog();
            }}
            color="secondary"
          >
            {t('common.actions.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteAllDialogOpen}
        onClose={closeDeleteAllDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>{t('customLinking.deleteAllDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('customLinking.deleteAllDialog.warning', { count: allRelationships.length, type: getCustomRelationshipType(selectedRelationshipType) })}
          </DialogContentText>
          <DialogContentText sx={{ mb: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
            {t('customLinking.deleteAllDialog.note')}
          </DialogContentText>
          <DialogContentText sx={{ mb: 3 }}>
            {t('customLinking.deleteAllPrompt', { phrase: t('customLinking.deleteAllPhrase') })}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            placeholder={t('customLinking.deleteAllDialog.placeholder', { phrase: t('customLinking.deleteAllPhrase') })}
            value={deleteAllConfirmText}
            onChange={(e) => setDeleteAllConfirmText(e.target.value)}
            error={deleteAllConfirmText.length > 0 && deleteAllConfirmText !== t('customLinking.deleteAllPhrase')}
            helperText={deleteAllConfirmText.length > 0 && deleteAllConfirmText !== t('customLinking.deleteAllPhrase') ? t('customLinking.deleteAllHelp', { phrase: t('customLinking.deleteAllPhrase') }) : ''}
          />
          {uploadProgress && (
            <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
              {t('customLinking.deletingBatch', uploadProgress)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteAllDialog} color="primary">
            {t('common.actions.cancel')}
          </Button>
          <Button
            onClick={handleDeleteAll}
            color="error"
            variant="contained"
            disabled={deleteAllConfirmText !== t('customLinking.deleteAllPhrase') || loading}
          >
            {loading ? <CircularProgress size={20} /> : t('customLinking.deleteAll')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomLinking;
